import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  branchListService,
  branchListShortService,
  branchDetailService,
  branchCreateService,
  branchUpdateService,
} from "./services";

type BranchListResponse = Awaited<ReturnType<typeof branchListService>>;
type BranchShortListResponse = Awaited<
  ReturnType<typeof branchListShortService>
>;
type BranchDetailResponse = Awaited<ReturnType<typeof branchDetailService>>;

export const useBranches = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof branchListService>[0];
  options?: QueryObserverOptions<unknown, unknown, BranchListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "branchListHooks",
    queryFn: () => branchListService(params),
    ...options,
  });

  return response as UseQueryResult<BranchListResponse>;
};

export const useShortBranches = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof branchListShortService>[0];
  options?: QueryObserverOptions<unknown, unknown, BranchShortListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "branchShortListHooks",
    queryFn: () => branchListShortService(params),
    ...options,
  });

  return response as UseQueryResult<BranchShortListResponse>;
};

export const useBranchDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof branchDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, BranchDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "branchDetailHooks",
    queryFn: () => branchDetailService(params),
    ...options,
  });

  return response as UseQueryResult<BranchDetailResponse>;
};

export const useBranchUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    BranchDetailResponse,
    unknown,
    Parameters<typeof branchUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "branchUpdateHooks",
    mutationFn: branchUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useBranchCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    BranchDetailResponse,
    unknown,
    Parameters<typeof branchCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "branchCreateHooks",
    mutationFn: branchCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
